/*!
 * Copyright (c) REWE Digital GmbH
 */
import { optimizeImageSrc, pixel } from "@rdss/core";
import Splide from "@splidejs/splide";
import { h, Build, Host } from "@stencil/core";
import { defaultMainOptions, defaultThumbnailOptions } from "./defaults";
/**
 * The vx-image-gallery displays two carousels - the main carousel and the thumbnail carousel.
 * When the main carousel moves, the active thumbnail will change. Also, thumbnails are
 * clickable to navigate the main carousel.
 * This component is based on the Splide-library (https://splidejs.com).
 */
// eslint-disable-next-line require-jsdoc
export class VxImageGallery {
    constructor() {
        /**
         * Merges the default with passed options.
         */
        this.initOptions = ({ speed, direction } = this) => {
            var _a;
            this.mainOptions = Object.assign(Object.assign(Object.assign({}, defaultMainOptions), this.mainOptions), { speed, direction });
            if (this.isTtbDirection())
                delete this.mainOptions.direction;
            this.thumbnailOptions = Object.assign(Object.assign(Object.assign({}, defaultThumbnailOptions), this.thumbnailOptions), { direction });
            this.displayThumbnails = ((_a = this.images) === null || _a === void 0 ? void 0 : _a.length) > 1 && this.displayThumbnails;
        };
        /**
         * Syncs main carousel with thumbnail carousel if property "syncSliders" is true.
         */
        this.setSync = () => this.syncSliders && this.mainSplide.sync(this.thumbnailSplide);
        /**
         * Checks if direction is set to tbb (top-to-bottom).
         */
        this.isTtbDirection = () => this.direction === 'ttb';
        /**
         * Mounts the main slider.
         */
        this.mountMainSplide = () => {
            var _a;
            if (this.mainSplideElement) {
                (_a = this.mainSplide) === null || _a === void 0 ? void 0 : _a.destroy();
                this.mainSplide = new Splide(this.mainSplideElement, this.mainOptions).mount();
            }
        };
        /**
         * Mounts the Thumbnail slider.
         */
        this.mountThumbnailSplide = () => {
            var _a;
            if (this.thumbnailSplideElement) {
                (_a = this.thumbnailSplide) === null || _a === void 0 ? void 0 : _a.destroy();
                this.thumbnailSplide = new Splide(this.thumbnailSplideElement, this.thumbnailOptions).mount();
            }
        };
        /**
         * Renders arrow.
         */
        this.arrow = () => (h("svg", { xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 40 40", width: "40", height: "40", focusable: "false" }, h("path", { d: this.thumbnailOptions.arrowPath })));
        this.optimizeImage = (imageSrc, height, options) => optimizeImageSrc(imageSrc, { height, quality: options.imageQuality, fit: options.imageFit });
        /**
         * Go to splice by index.
         */
        this.gotoSlide = (index) => this.mainSplide.go(index);
        this.images = [];
        this.mainOptions = undefined;
        this.thumbnailOptions = undefined;
        this.direction = 'ltr';
        this.speed = 1200;
        this.displayThumbnails = true;
        this.thumbnailSize = 150;
        this.syncSliders = true;
    }
    /**
     * @inheritDoc
     */
    componentWillLoad() {
        this.initOptions();
    }
    /**
     * @inheritDoc
     */
    componentDidLoad() {
        var _a;
        if (!this.mainSplide && ((_a = this.images) === null || _a === void 0 ? void 0 : _a.length)) {
            this.initSliders(this.images, []);
        }
    }
    /**
     * Initializes the sliders.
     */
    initSliders(newVal, oldVal) {
        // Do nothing if the images did not change
        if (JSON.stringify(oldVal) === JSON.stringify(newVal))
            return;
        setTimeout(() => {
            this.initOptions();
            this.mountMainSplide();
            if (this.displayThumbnails && this.images.length > 1) {
                this.mountThumbnailSplide();
                this.setSync();
            }
        });
    }
    /**
     * Renders the component.
     *
     * @returns {JSX.Element} - The rendered component.
     */
    render() {
        return (h(Host, { key: '2e76e0113197bd5d111d0de47c368e7b02946e4a' }, h("div", { key: 'ff47f655d05eda783f74f4466a3dd308d06258c4', class: { 'image-gallery': true, 'with-thumbnails': this.displayThumbnails, [this.direction]: true } }, h("section", { key: '18b219559fab6475a16b6a13f8de0f3403b629e6', id: "main-slider", class: "splide", "aria-label": this.mainOptions.label, ref: (el) => { this.mainSplideElement = el; } }, h("div", { key: 'b3e9cb26bcbfeeac5b8b2c42677d45e2fd620447', class: "splide__track" }, h("ul", { key: 'd40ddfec823dc48c06bb5504cae3b5851a7bc702', class: "splide__list" }, this.images.map(item => {
            var _a;
            return (h("li", { class: "splide__slide", style: { maxHeight: pixel(this.mainOptions.height) } }, h("img", { alt: item.description, src: this.optimizeImage(item.file, Number((_a = this.mainOptions.height) !== null && _a !== void 0 ? _a : 600), this.mainOptions) })));
        })))), this.displayThumbnails &&
            h("section", { id: "thumbnail-slider", class: "splide", "aria-label": this.thumbnailOptions.label, style: {
                    maxWidth: this.isTtbDirection() ? pixel(this.thumbnailSize) : '100%',
                    maxHeight: pixel(this.isTtbDirection() ? this.thumbnailOptions.height : this.thumbnailSize),
                }, ref: (el) => { this.thumbnailSplideElement = el; } }, h("div", { class: { splide__arrows: true, 'splide__arrows--ttb': this.isTtbDirection() } }, h("button", { class: "splide__arrow splide__arrow--prev", type: "button", disabled: true, "aria-label": this.mainOptions.labelPrevSlideButton, "aria-controls": "thumbnail-slider-track" }, this.arrow()), h("button", { class: "splide__arrow splide__arrow--next", type: "button", "aria-label": this.mainOptions.labelNextSlideButton, "aria-controls": "thumbnail-slider-track" }, this.arrow())), h("div", { class: { splide__track: true, 'splide__track--ttb': this.isTtbDirection() } }, h("ul", { class: "splide__list thumbnails" }, this.images.map((item, index) => (h("li", { onClick: this.gotoSlide.bind(this, index), class: {
                    splide__slide: true,
                    thumbnail: true,
                    'is-active': Build.isServer && (this.direction === 'rtl' && index === this.images.length - 1 ||
                        this.direction !== 'rtl' && index === 0),
                }, style: {
                    [this.isTtbDirection() ? 'maxHeight' : 'maxWidth']: pixel(this.thumbnailSize),
                    marginBottom: pixel(this.thumbnailOptions.gap),
                    marginRight: pixel(this.thumbnailOptions.gap),
                } }, h("img", { src: this.optimizeImage(item.file, this.thumbnailSize, this.thumbnailOptions), alt: item.description }))))))))));
    }
    static get is() { return "vx-image-gallery"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["vx-image-gallery.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["vx-image-gallery.css"]
        };
    }
    static get properties() {
        return {
            "images": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "CarouselImages",
                    "resolved": "CarouselImage[]",
                    "references": {
                        "CarouselImages": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/vx-image-gallery/types/index.ts::CarouselImages"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Holds the images for the carousel."
                },
                "defaultValue": "[]"
            },
            "mainOptions": {
                "type": "unknown",
                "mutable": true,
                "complexType": {
                    "original": "Partial<CarouselOptions>",
                    "resolved": "{ [x: string]: any; labelNextSlideButton?: string; labelPrevSlideButton?: string; imageQuality?: number; imageFit?: string; type?: string; role?: string; waitForTransition?: boolean; autoWidth?: boolean; autoHeight?: boolean; start?: number; arrowPath?: string; autoplay?: boolean | \"pause\"; interval?: number; pauseOnHover?: boolean; pauseOnFocus?: boolean; resetProgress?: boolean; lazyLoad?: boolean | \"nearby\" | \"sequential\"; preloadPages?: number; keyboard?: boolean | \"global\" | \"focused\"; wheel?: boolean; wheelMinThreshold?: number; wheelSleep?: number; releaseWheel?: boolean; direction?: \"ltr\" | \"rtl\" | \"ttb\"; cover?: boolean; slideFocus?: boolean; isNavigation?: boolean; trimSpace?: boolean | \"move\"; omitEnd?: boolean; updateOnMove?: boolean; mediaQuery?: \"min\" | \"max\"; focusableNodes?: string; noDrag?: string; live?: boolean; useScroll?: boolean; breakpoints?: Record<string | number, ResponsiveOptions>; reducedMotion?: Options; classes?: Record<string, string>; i18n?: Record<string, string>; label?: string; labelledby?: string; speed?: number; rewind?: boolean; rewindSpeed?: number; rewindByDrag?: boolean; width?: string | number; height?: string | number; fixedWidth?: string | number; fixedHeight?: string | number; heightRatio?: number; perPage?: number; perMove?: number; clones?: number; cloneStatus?: boolean; focus?: number | \"center\"; gap?: string | number; padding?: string | number | { left?: string | number; right?: string | number; } | { top?: string | number; bottom?: string | number; }; arrows?: boolean; pagination?: boolean; paginationKeyboard?: boolean; paginationDirection?: \"ltr\" | \"rtl\" | \"ttb\"; easing?: string; easingFunc?: (t: number) => number; drag?: boolean | \"free\"; snap?: boolean; dragMinThreshold?: number | { mouse: number; touch: number; }; flickPower?: number; flickMaxPages?: number; destroy?: boolean | \"completely\"; }",
                    "references": {
                        "Partial": {
                            "location": "global",
                            "id": "global::Partial"
                        },
                        "CarouselOptions": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/vx-image-gallery/types/index.ts::CarouselOptions"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [{
                            "name": "see",
                            "text": "https://splidejs.com/guides/options/#options"
                        }],
                    "text": "The options for the main carousel."
                }
            },
            "thumbnailOptions": {
                "type": "unknown",
                "mutable": true,
                "complexType": {
                    "original": "Partial<CarouselOptions>",
                    "resolved": "{ [x: string]: any; labelNextSlideButton?: string; labelPrevSlideButton?: string; imageQuality?: number; imageFit?: string; type?: string; role?: string; waitForTransition?: boolean; autoWidth?: boolean; autoHeight?: boolean; start?: number; arrowPath?: string; autoplay?: boolean | \"pause\"; interval?: number; pauseOnHover?: boolean; pauseOnFocus?: boolean; resetProgress?: boolean; lazyLoad?: boolean | \"nearby\" | \"sequential\"; preloadPages?: number; keyboard?: boolean | \"global\" | \"focused\"; wheel?: boolean; wheelMinThreshold?: number; wheelSleep?: number; releaseWheel?: boolean; direction?: \"ltr\" | \"rtl\" | \"ttb\"; cover?: boolean; slideFocus?: boolean; isNavigation?: boolean; trimSpace?: boolean | \"move\"; omitEnd?: boolean; updateOnMove?: boolean; mediaQuery?: \"min\" | \"max\"; focusableNodes?: string; noDrag?: string; live?: boolean; useScroll?: boolean; breakpoints?: Record<string | number, ResponsiveOptions>; reducedMotion?: Options; classes?: Record<string, string>; i18n?: Record<string, string>; label?: string; labelledby?: string; speed?: number; rewind?: boolean; rewindSpeed?: number; rewindByDrag?: boolean; width?: string | number; height?: string | number; fixedWidth?: string | number; fixedHeight?: string | number; heightRatio?: number; perPage?: number; perMove?: number; clones?: number; cloneStatus?: boolean; focus?: number | \"center\"; gap?: string | number; padding?: string | number | { left?: string | number; right?: string | number; } | { top?: string | number; bottom?: string | number; }; arrows?: boolean; pagination?: boolean; paginationKeyboard?: boolean; paginationDirection?: \"ltr\" | \"rtl\" | \"ttb\"; easing?: string; easingFunc?: (t: number) => number; drag?: boolean | \"free\"; snap?: boolean; dragMinThreshold?: number | { mouse: number; touch: number; }; flickPower?: number; flickMaxPages?: number; destroy?: boolean | \"completely\"; }",
                    "references": {
                        "Partial": {
                            "location": "global",
                            "id": "global::Partial"
                        },
                        "CarouselOptions": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/vx-image-gallery/types/index.ts::CarouselOptions"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [{
                            "name": "see",
                            "text": "https://splidejs.com/guides/options/#options"
                        }],
                    "text": "The options for the thumbnail carousel."
                }
            },
            "direction": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "'ltr' | 'rtl' | 'ttb'",
                    "resolved": "\"ltr\" | \"rtl\" | \"ttb\"",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [{
                            "name": "see",
                            "text": "https://splidejs.com/guides/options/#direction"
                        }, {
                            "name": "default",
                            "text": "'ttb'"
                        }],
                    "text": "The direction of the gallery."
                },
                "attribute": "direction",
                "reflect": false,
                "defaultValue": "'ltr'"
            },
            "speed": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The duration of the slide-animation."
                },
                "attribute": "speed",
                "reflect": false,
                "defaultValue": "1200"
            },
            "displayThumbnails": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [{
                            "name": "default",
                            "text": "true"
                        }],
                    "text": "Displays thumbnails."
                },
                "attribute": "display-thumbnails",
                "reflect": false,
                "defaultValue": "true"
            },
            "thumbnailSize": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [{
                            "name": "default",
                            "text": "150"
                        }],
                    "text": "The size of a thumbnail image."
                },
                "attribute": "thumbnail-size",
                "reflect": false,
                "defaultValue": "150"
            },
            "syncSliders": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [{
                            "name": "default",
                            "text": "true"
                        }],
                    "text": "Syncs main carousel with thumbnail carousel."
                },
                "attribute": "sync-sliders",
                "reflect": false,
                "defaultValue": "true"
            }
        };
    }
    static get watchers() {
        return [{
                "propName": "images",
                "methodName": "initSliders"
            }];
    }
}
//# sourceMappingURL=vx-image-gallery.js.map
